import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, Building, Users, User, Search, RefreshCw, Save, RotateCcw,
  ChevronDown, ChevronRight, Check, X, AlertTriangle, History, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PermissionsHub = () => {
  const { user } = useAuth();
  // Main Hub uses different token
  const token = localStorage.getItem('nexus_main_hub_token') || localStorage.getItem('nexus_token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState([]);
  const [features, setFeatures] = useState({});
  const [templates, setTemplates] = useState({});
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  const [targetType, setTargetType] = useState('company');
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [targetPermissions, setTargetPermissions] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);
  
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [modulesRes, featuresRes, templatesRes, companiesRes] = await Promise.all([
        fetch(`${API_URL}/api/permissions/modules`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/permissions/features`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/permissions/templates`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/main-hub/companies`, { headers, credentials: 'include' })
      ]);
      
      if (modulesRes.ok) setModules(await modulesRes.json());
      if (featuresRes.ok) setFeatures(await featuresRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (companiesRes.ok) setCompanies(await companiesRes.json());
    } catch (err) {
      toast.error('Failed to load permissions data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/main-hub/users`, { headers, credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch (err) {
      console.error('Failed to fetch users');
    }
  }, [token]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/permissions/audit-logs?limit=50`, { headers, credentials: 'include' });
      if (res.ok) setAuditLogs(await res.json());
    } catch (err) {
      console.error('Failed to fetch audit logs');
    }
  }, [token]);

  useEffect(() => {
    fetchInitialData();
    fetchAuditLogs();
  }, [fetchInitialData, fetchAuditLogs]);

  useEffect(() => {
    if (targetType === 'user') {
      fetchUsers();
    }
  }, [targetType, fetchUsers]);

  const selectTarget = async (type, id) => {
    setSelectedTarget(id);
    try {
      const endpoint = type === 'company' 
        ? `${API_URL}/api/permissions/company/${id}`
        : `${API_URL}/api/permissions/user/${id}`;
      
      const res = await fetch(endpoint, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTargetPermissions(data);
      }
    } catch (err) {
      toast.error('Failed to load permissions');
    }
  };

  const toggleModule = (moduleId) => {
    if (!targetPermissions) return;
    setTargetPermissions(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [moduleId]: !prev.modules[moduleId]
      }
    }));
  };

  const toggleFeature = (moduleId, featureId) => {
    if (!targetPermissions) return;
    setTargetPermissions(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [moduleId]: {
          ...(prev.features[moduleId] || {}),
          [featureId]: !(prev.features[moduleId]?.[featureId] ?? false)
        }
      }
    }));
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleSave = async () => {
    if (!targetPermissions || !selectedTarget) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/permissions/update`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          target_type: targetType,
          target_id: selectedTarget,
          module_permissions: targetPermissions.modules,
          feature_permissions: targetPermissions.features
        })
      });
      
      if (res.ok) {
        toast.success('Permissions saved successfully');
        fetchAuditLogs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save permissions');
      }
    } catch (err) {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedTarget || !window.confirm('Reset permissions to defaults? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/permissions/${targetType}/${selectedTarget}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Permissions reset to defaults');
        selectTarget(targetType, selectedTarget);
        fetchAuditLogs();
      }
    } catch (err) {
      toast.error('Failed to reset permissions');
    }
  };

  const applyTemplate = (templateName) => {
    const template = templates[templateName];
    if (!template || !targetPermissions) return;
    
    setTargetPermissions(prev => ({
      ...prev,
      modules: { ...template.modules },
      features: { ...template.features }
    }));
    toast.success(`Applied "${templateName}" template`);
  };

  const filteredList = targetType === 'company' 
    ? companies.filter(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    : users.filter(u => u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.username?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Main Hub uses different token - check if logged in via Main Hub
  const mainHubToken = localStorage.getItem('nexus_main_hub_token');
  const isMainHub = !!mainHubToken;

  if (!isMainHub) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">Only Main Hub administrators can access the Permissions Hub.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-hub">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-400" /> Permissions Hub
          </h1>
          <p className="text-slate-400 text-sm">Manage module and feature access for companies and users</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowAuditLog(!showAuditLog)}
            className="border-slate-600"
          >
            <History className="w-4 h-4 mr-2" /> Audit Log
          </Button>
          <Button variant="outline" onClick={fetchInitialData} className="border-slate-600">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Target Selection Panel */}
        <div className="bg-slate-800/60 rounded-lg border border-slate-700 p-4">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => { setTargetType('company'); setSelectedTarget(null); setTargetPermissions(null); }}
              className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                targetType === 'company' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Building className="w-4 h-4 inline mr-1" /> Companies
            </button>
            <button
              onClick={() => { setTargetType('user'); setSelectedTarget(null); setTargetPermissions(null); }}
              className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                targetType === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Users className="w-4 h-4 inline mr-1" /> Users
            </button>
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={`Search ${targetType === 'company' ? 'companies' : 'users'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-700 border-slate-600 text-white"
            />
          </div>

          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {loading ? (
              <p className="text-slate-400 text-center py-4">Loading...</p>
            ) : filteredList.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No {targetType === 'company' ? 'companies' : 'users'} found</p>
            ) : (
              filteredList.map(item => (
                <button
                  key={item.id}
                  onClick={() => selectTarget(targetType, item.id)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${
                    selectedTarget === item.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {targetType === 'company' ? <Building className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.name || item.username}</p>
                      {targetType === 'user' && item.role && (
                        <p className="text-xs opacity-70">{item.role.replace(/_/g, ' ')}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Permissions Editor Panel */}
        <div className="lg:col-span-2 bg-slate-800/60 rounded-lg border border-slate-700">
          {!targetPermissions ? (
            <div className="flex items-center justify-center h-[500px] text-slate-400">
              <div className="text-center">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a {targetType} to manage permissions</p>
              </div>
            </div>
          ) : (
            <>
              {/* Editor Header */}
              <div className="px-4 py-3 border-b border-slate-700 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-white font-medium">
                    {targetType === 'company' ? targetPermissions.company_name : targetPermissions.user_name}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {targetPermissions.is_default ? 'Using default permissions' : 'Custom permissions applied'}
                    {targetType === 'user' && ` • Role: ${targetPermissions.role?.replace(/_/g, ' ')}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <select 
                    onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                    className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                    defaultValue=""
                  >
                    <option value="">Apply Template...</option>
                    <option value="department_admin">Department Admin</option>
                    <option value="field_investigator">Field Investigator</option>
                    <option value="client">Client</option>
                  </select>
                  <Button variant="outline" size="sm" onClick={handleReset} className="border-slate-600 text-slate-300">
                    <RotateCcw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>

              {/* Module List */}
              <div className="p-4 max-h-[450px] overflow-y-auto">
                <div className="space-y-2">
                  {modules.map(mod => {
                    const moduleEnabled = targetPermissions.modules[mod.id] ?? false;
                    const moduleFeatures = features[mod.id] || [];
                    const isExpanded = expandedModules[mod.id];
                    
                    return (
                      <div key={mod.id} className="bg-slate-700/50 rounded-lg overflow-hidden">
                        {/* Module Header */}
                        <div className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-3 flex-1">
                            {moduleFeatures.length > 0 && (
                              <button 
                                onClick={() => toggleModuleExpand(mod.id)}
                                className="text-slate-400 hover:text-white"
                              >
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                            )}
                            <div>
                              <p className="text-white font-medium">{mod.name}</p>
                              <p className="text-xs text-slate-400">{mod.description}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleModule(mod.id)}
                            className={`w-12 h-6 rounded-full transition-colors ${
                              moduleEnabled ? 'bg-green-500' : 'bg-slate-600'
                            }`}
                          >
                            <span className={`block w-4 h-4 bg-white rounded-full transform transition-transform ${
                              moduleEnabled ? 'translate-x-7' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        
                        {/* Feature List */}
                        {isExpanded && moduleFeatures.length > 0 && (
                          <div className="px-4 pb-3 pt-1 border-t border-slate-600">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {moduleFeatures.map(feat => {
                                const featureEnabled = targetPermissions.features[mod.id]?.[feat] ?? false;
                                return (
                                  <button
                                    key={feat}
                                    onClick={() => toggleFeature(mod.id, feat)}
                                    disabled={!moduleEnabled}
                                    className={`flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                                      !moduleEnabled 
                                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
                                        : featureEnabled 
                                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                    }`}
                                  >
                                    {featureEnabled ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                                    <span className="capitalize">{feat.replace(/_/g, ' ')}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Audit Log Panel */}
      {showAuditLog && (
        <div className="bg-slate-800/60 rounded-lg border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" /> Permission Change History
            </h3>
            <button onClick={() => setShowAuditLog(false)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-700/50">
                  <th className="text-left px-3 py-2 text-slate-400">Timestamp</th>
                  <th className="text-left px-3 py-2 text-slate-400">Changed By</th>
                  <th className="text-left px-3 py-2 text-slate-400">Target</th>
                  <th className="text-left px-3 py-2 text-slate-400">Action</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-8 text-slate-400">No audit logs found</td></tr>
                ) : (
                  auditLogs.map(log => (
                    <tr key={log.id} className="border-t border-slate-700">
                      <td className="px-3 py-2 text-slate-300">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-3 py-2 text-slate-300">{log.changed_by_name}</td>
                      <td className="px-3 py-2 text-slate-300">
                        <span className="capitalize">{log.target_type}</span>: {log.target_name || log.target_id}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          log.action === 'permission_reset' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {log.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsHub;
